import React, { Fragment, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import { Formik, Field, Form } from 'formik';
import i18n from '../../../i18n';
import { GlobalContext } from 'context/GlobalProvider';
import ButtonWithAsync from 'components/UI/ButtonWithAsync/ButtonWithAsync';
import ReachingLimitDialog from 'components/UI/ReachingLimitDialog/ReachingLimitDialog';
import Alert from 'components/UI/Antd/Alert/Alert';
import AntdCheckboxWithFormik from 'components/UI/Antd/AntdCheckboxWithFormik';
import { AntInput } from 'components/UI/Antd/AntdInputWithFormik';
import Button from 'components/UI/Antd/Button/Button';
import { getGuestReservationSchema } from 'library/helpers/formUtils';
import reservationService from 'services/reservation.js';
import {
  FormWrapper,
  Title,
  SubTitle,
  FieldWrapper,
  SwitchWrapper,
  Label
} from './MembershipsWizard.style.js';
import { message } from 'antd';
import isEmpty from 'lodash/isEmpty';
import useAmplitude from 'library/hooks/useAmplitude';
import { useSessionStorage } from 'library/hooks/useSessionStorage';
import ProdAlertMessage from "../Reservation/ProdAlertMessage";
import MyModal from "../../../components/UI/Modal/Modal";
import {URL_DATE_FORMAT} from "../../../library/helpers/utils";

message.config({
  top: 100,
  duration: 7,
  maxCount: 1
});

const AsyncButton = ButtonWithAsync(Button);

const initialValues = {
  email: '',
  termsAndConditions: false
};

const GuestReservationForm = props => {
  const { t } = useTranslation();
  const { termsOfServiceLink, privacyPolicyLink } = useContext(GlobalContext);
  const { values, submitCount, submitForm, error, onSignInAuth } = props;

  return (
    <Form className={error ? 'error' : ''}>
      <Field
        component={AntInput}
        className='input-field'
        name='email'
        type='email'
        size='large'
        placeholder={t('YOUR_EMAIL_PLACEHOLDER')}
        defaultValue={values.email}
        submitCount={submitCount}
        hasFeedback
      />

      {error && (
        <Alert
          message={
            <Fragment>
              {error.message}{' '}
              <Button
                className='please-login-btn'
                type='link'
                htmlType='button'
                onClick={onSignInAuth}
              >
                {t('PLEASE_LOGIN')}
              </Button>
            </Fragment>
          }
          type='error'
          showIcon
        />
      )}

      <FieldWrapper>
        <SwitchWrapper>
          <Field
            className='terms-field'
            component={AntdCheckboxWithFormik}
            name='termsAndConditions'
            checked={values.termsAndConditions}
          >
            <Label>
              {t('GUEST_RESERVATION_AGREE_LABEL')}
              <a
                href={termsOfServiceLink || '#'}
                target='_blank'
                onClick={e => e.stopPropagation()}
              >
                {t('TERMS_OF_USE_LINK')}
              </a>{' '}
              &amp;
              <a
                href={privacyPolicyLink || '#'}
                target='_blank'
                onClick={e => e.stopPropagation()}
              >
                {t('PRIVACY_POLICY_LINK')}
              </a>
            </Label>
          </Field>
        </SwitchWrapper>
      </FieldWrapper>

      <AsyncButton
        asyncAction={submitForm}
        className='continue-btn'
        htmlType='button'
        type='primary'
        size='large'
        label={t('CONTINUE')}
      />
    </Form>
  );
};

const WizardGuest = ({
  values,
  date,
  match,
  onSignInAuth,
  onSignUpAuth,
  onOpenSuccess,
  onCloseDrawer,
  calculatedPrice,
  location,
  selectedMarina,
}) => {
  const { t } = useTranslation();
  const guestReservationSchema = getGuestReservationSchema(i18n);
  const { getSingleMarina } = useContext(GlobalContext);
  const currentMarina = getSingleMarina(selectedMarina || match.params.id);

  const [error, setError] = useState(null);
  const [limitDialogOpened, setLimitDialogOpened] = useState(false);
  const { sendToAmplitude } = useAmplitude();
  const [prodAlertProps, setProdAlertProps] = useState(undefined);

  const [numberOfMarinasViewed, setNumberOfMarinasViewed] = useSessionStorage(
    'numberOfMarinasViewed',
    []
  );

  const handleSubmit = async formProps => {
    const { hull_type, length, beam, draft, vessel_name: name } = values;
    const { email } = formProps;
    const data = {
      start_date: moment(date.setStartDate, URL_DATE_FORMAT).format('YYYY-MM-DD'),
      end_date: moment(date.setEndDate, URL_DATE_FORMAT).format('YYYY-MM-DD'),
      marina_id: currentMarina.id,
      boater: { email: email.toLowerCase() },
      vessel: {
        hull_type,
        length,
        beam,
        draft,
        name
      }
    };

    try {
      const res = await reservationService.createGuest(data);
      if (res && res.error_type) {
        if (res.error_type === 'BOATER_CONNECTED_REDIRECT_TO_LOGIN') {
          setError({ message: t('EMAIL_IS_ALREADY_IN_USE') });
        } else if (res.error_type === 'NO_PERMISSION') {
          setLimitDialogOpened(true);
        } else if (
          res.error_code &&
          res.error_code === 'RESERVATION_DUPLICATE_DATA'
        ) {
          setError({
            message: t('DUPLICATE_RESERVATION_ERROR', {
              marina_name: currentMarina.name
            })
          });
        } else {
          message.error(t('ERROR_CREATING_REQUEST'));
        }
        return Promise.reject(t('ERROR_CREATING_REQUEST'));
      } else {
        setError(null);
        onOpenSuccess();

        // Amplitude event
        let origin = '';
        if (!location.state) {
          origin = 'direct_link';
        } else if (location.state.ref) {
          origin = location.state.ref;
        } else if (location.state.from === 'top_marinas') {
          origin = 'top_marinas';
        }

        const today = moment().startOf('day')
        const eventProperties = {
          reservation_id: res.id,
          marina_id: currentMarina.id,
          marina_name: currentMarina.name,
          marina_country: currentMarina.country,
          marina_association: currentMarina.association.name,
          hull_type,
          boat_loa: length,
          boat_beam: beam,
          boat_draft: draft,
          number_of_nights: moment(data.end_date).diff(
            moment(data.start_date),
            'days'
          ),
          marinas_viewed_before:
            numberOfMarinasViewed.length === 0
              ? 0
              : numberOfMarinasViewed.length - 1,
          origin,
          boater_type: 'guest',
          price: !isEmpty(calculatedPrice)
            ? calculatedPrice.total_to_pay
            : '**',
          redeem_nights: 'n/a',
          days_before: moment(data.start_date).diff(today, 'days'),
          error: true // trigger the sign up popup, product team wanted this to be error.
        };

        sendToAmplitude({
          eventProperties,
          eventName: 'reservation_created'
        });
        setNumberOfMarinasViewed([]);
        return Promise.resolve();
      }
    } catch (err) {
      return Promise.reject(t('ERROR_CREATING_REQUEST'));
    }
  };

  const onSubmit = async formProps => {
    const { email } = formProps;
    if(process.env.REACT_APP_FIREBASE_ENV === 'PRODUCTION' && email && email.toLowerCase().includes('@pickapier.com')) {
      setProdAlertProps(formProps);
    } else {
      await handleSubmit(formProps)
    }
  }

  const onHandleDialog = () => {
    setLimitDialogOpened(false);
    onSignUpAuth();
  };

  const onCloseLimitDialog = () => {
    setLimitDialogOpened(false);
    onCloseDrawer();
    setTimeout(() => {
      // workaround: fix the strange bug from ant design
      // in some reason body gets overflow:hidden after popup closing:
      // https://github.com/ant-design/ant-design/issues/21539
      document.body.style.overflow = '';
      document.body.style.position = 'static';
      document.body.style.top = 'auto';
    }, 500);
  };

  return (
    <FormWrapper className='guest-reservation-flow'>
      <Title>{t('WELCOME_GUEST')}</Title>
      <SubTitle>
        {t('WE_NEED_EMAIL_TO_SEND_RESERVATION_TO')} <b>{currentMarina.name}</b>
      </SubTitle>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={guestReservationSchema}
      >
        {props => (
          <GuestReservationForm {...{ ...props, error, onSignInAuth }} />
        )}
      </Formik>
      <ReachingLimitDialog
        visible={limitDialogOpened}
        title={t('REACHED_MAX_GUEST_REQUESTS_TITLE')}
        onCancel={onCloseLimitDialog}
        subtitle={
          <Fragment>
            <p>{t('REACHED_MAX_GUEST_REQUESTS_DESCRIPTION')}</p>
          </Fragment>
        }
        actions={
          <Button htmlType='button' type='primary' onClick={onHandleDialog}>
            {t('SIGN_UP_BUTTON')}
          </Button>
        }
      />
      <MyModal onClose={() => setProdAlertProps(undefined)} visible={!!prodAlertProps}>
        <ProdAlertMessage
            handleClose={() => setProdAlertProps(undefined)}
            handleSubmit={() => {
              setProdAlertProps(undefined);
              handleSubmit(prodAlertProps);
            }}
        />
      </MyModal>
    </FormWrapper>
  );
};

export default withRouter(WizardGuest);
